import React, { useState, useEffect, useRef } from "react";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import { Input } from "antd";
import { API_URLS } from "../../../api/apiUrls";
import { putApi } from "../../../api/api_instance";
import { AcceptDialog } from "../../../components/Elements/DailogBox/AcceptDialog";
import { ReactComponent as UploadButtonIcon } from "../../../assets/svgs/upload.svg";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard"; // Import PopupCard

const EditReportType = ({ onOpen, onClose, projectData, onUpdate, onError }) => {
  const [name, setProjectType] = useState("");
  const [description, setDescription] = useState("");
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [existingPdfUrl, setExistingPdfUrl] = useState("");
  
  // Popup state
  const [popup, setPopup] = useState({
    visible: false,
    type: "", // "success" | "error" | "warning"
    title: "",
    message: "",
  });

  const [errorMessage, setErrorMessage] = useState({
    name: "",
    description: "",
    pdf: "",
  });

  const fileInputRef = useRef(null);

  // Show popup function
  const showPopup = (type, title, message, duration = 3000) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => {
      setPopup({ visible: false, type: "", title: "", message: "" });
    }, duration);
  };

  useEffect(() => {
    if (projectData) {
      console.log("Project Data:", projectData);
      setProjectType(projectData.report.name || "");
      setDescription(projectData.report.description || "");
      setExistingPdfUrl(projectData.pdf || "");
      setDownloadUrl(projectData.downloadUrl || "");
    }
  }, [projectData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setPdf(file);
      setErrorMessage((prev) => ({ ...prev, pdf: "" }));
    } else {
      setPdf(null);
      setErrorMessage((prev) => ({
        ...prev,
        pdf: "Only PDF files are allowed",
      }));
      showPopup("error", "Invalid File", "Only PDF files are allowed");
    }
  };

  const handleViewPdf = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else if (existingPdfUrl) {
      window.open(existingPdfUrl, '_blank');
    } else {
      showPopup("warning", "No PDF", "No PDF file available to view");
    }
  };

  const getPdfFileName = (url) => {
    if (!url) return "";
    const fileName = url.split('/').pop().split('?')[0];
    return decodeURIComponent(fileName);
  };

  const handleSave = async () => {
    let isValid = true;
    const errors = { name: "", description: "", pdf: "" };

    if (!name.trim()) {
      errors.name = "Report Type is required";
      isValid = false;
    }
    if (!description.trim()) {
      errors.description = "Description is required";
      isValid = false;
    }
    if (!pdf && !existingPdfUrl) {
      errors.pdf = "PDF is required";
      isValid = false;
    }

    setErrorMessage(errors);
    
    if (!isValid) {
      showPopup("warning", "Validation Error", "Please upload pdf file");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("description", description);

      if (pdf) {
        formData.append("pdf", pdf);
      }

      const response = await putApi({
        apiUrl: API_URLS.UPDATE_REPORT_TYPE(projectData.report._id),
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.success) {
        showPopup("success", "Success", "Report Type updated successfully");
        setTimeout(() => {
          onUpdate();
          onClose();
        }, 1500);
      } else {
        showPopup("error", "Update Failed", response.message || "Failed to update Report Type");
        if (onError) onError(response.message);
      }
    } catch (error) {
      console.error("Update error:", error);
      showPopup("error", "Error", error.message || "Something went wrong");
      if (onError) onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DailogBox open={onOpen} onCancel={onClose} width="90%" maxWidth="600px">
        <div>
          <h3 className="text-xl font-bold text-[#06513D]">
            Edit Report Type
          </h3>
        </div>
        <div className="rounded-xl border mt-8 px-12">
          <div className="flex flex-col gap-6 mt-2">
            <Input
              value={name}
              onChange={(e) => setProjectType(e.target.value)}
              placeholder="Report Type"
              className="h-14 rounded-lg"
            />
            {errorMessage.name && (
              <div className="text-red-500 text-sm">{errorMessage.name}</div>
            )}

            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              className="h-14 rounded-lg"
            />
            {errorMessage.description && (
              <div className="text-red-500 text-sm">
                {errorMessage.description}
              </div>
            )}
          </div>

          {/* File Upload with View Button */}
          <div className="mt-6">
            <div className="bg-gray-100 border border-gray-300 rounded-md flex justify-between items-center">
              <div className="text-gray-500 p-3 flex-1 truncate">
                {pdf
                  ? pdf.name
                  : existingPdfUrl
                    ? getPdfFileName(existingPdfUrl)
                    : "Please select your PDF file"
                }
              </div>

              <div className="flex gap-2">
                {/* View Button */}
                

                {/* Upload Button */}
                <button
                  type="button"
                  className="bg-[#06513D] hover:bg-[#053d2e] text-white px-10 rounded-r py-3 h-full flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  <div className="[&>svg]:fill-white [&>svg>path]:fill-white">
                    <UploadButtonIcon />
                  </div>
                </button>
                {(downloadUrl || existingPdfUrl) && (
                  <button
                    type="button"
                    onClick={handleViewPdf}
                    className="bg-[#06513D] hover:bg-[#053d2e] text-white px-4 py-3 h-full flex items-center gap-2 rounded"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                    View
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {errorMessage.pdf && (
              <div className="text-red-500 text-sm mt-1">
                {errorMessage.pdf}
              </div>
            )}
          </div>

          <div className="flex justify-center gap-6 p-5">
            <CustomButton
              buttonType="button"
              variant="cancel"
              buttonText="Cancel"
              onClick={onClose}
              className="text-base font-normal h-[50px] w-[160px]"
            />

            <CustomButton
              buttonType="button"
              variant="ok"
              buttonText={loading ? "Saving..." : "Save"}
              onClick={handleSave}
              className="text-base font-normal h-[50px] w-[160px]"
              disabled={loading}
            />
          </div>
        </div>
      </DailogBox>

      {/* Popup Card */}
      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
         
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
          />
        </div>
      )}
    </>
  );
};

export default EditReportType;